import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel'
import type { CarouselProps } from './Carousel.types'
import { IconButton } from '@sunwebgroup/uds-ui/icon-button'
import cn from 'classnames'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './Carousel.module.css'

const DOT_WIDTH = 8
const DOT_SPACING = 8
const DOTS_VISIBLE = 5
const DOTS_CENTER = Math.floor(DOTS_VISIBLE / 2)

export function Carousel({
  children,
  showNavigation = true,
  showDots = true,
  controlsPosition = 'inside',
  loop = false,
  dragFree = false,
  watchDrag = true,
  slideSize = '100%',
  slideSpacing = '0px',
  className,
  style,
  onSlideChange,
  ...attributes
}: CarouselProps) {
  const options = useMemo<EmblaOptionsType>(() => ({
    loop,
    dragFree,
    watchDrag,
    align: 'start',
    containScroll: loop ? false : 'trimSnaps',
  }), [loop, dragFree, watchDrag])

  const [emblaRef, emblaApi] = useEmblaCarousel(options)

  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)
  const [dotsOffset, setDotsOffset] = useState(0)

  const onSlideChangeRef = useRef(onSlideChange)
  onSlideChangeRef.current = onSlideChange
  const didInit = useRef(false)

  const onInit = useCallback((api: EmblaCarouselType) => {
    setScrollSnaps(api.scrollSnapList())
  }, [])

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setSelectedIndex(api.selectedScrollSnap())
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi)
      return

    onInit(emblaApi)
    onSelect(emblaApi)
    emblaApi.on('reInit', onInit).on('select', onSelect)

    return () => {
      emblaApi.off('reInit', onInit).off('select', onSelect)
    }
  }, [emblaApi, onInit, onSelect])

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true
      return
    }
    onSlideChangeRef.current?.(selectedIndex)
  }, [selectedIndex])

  useEffect(() => {
    const total = scrollSnaps.length
    if (total <= DOTS_VISIBLE) {
      setDotsOffset(0)
      return
    }
    if (selectedIndex > DOTS_CENTER && selectedIndex < total - DOTS_CENTER) {
      setDotsOffset((selectedIndex - DOTS_CENTER) * (DOT_WIDTH + DOT_SPACING))
    }
    else if (selectedIndex >= total - DOTS_CENTER) {
      setDotsOffset((total - DOTS_VISIBLE) * (DOT_WIDTH + DOT_SPACING))
    }
    else {
      setDotsOffset(0)
    }
  }, [selectedIndex, scrollSnaps.length])

  const isInside = controlsPosition === 'inside'
  const hasMultipleSlides = scrollSnaps.length > 1

  const prevButton = showNavigation && hasMultipleSlides && (
    <IconButton
      className={cn(styles.navButton, styles.prevButton)}
      variant={isInside ? 'overlay-neutral' : 'secondary'}
      size="S"
      glyph="ChevronLeft"
      onClick={() => emblaApi?.scrollPrev()}
      disabled={!canScrollPrev}
    />
  )

  const nextButton = showNavigation && hasMultipleSlides && (
    <IconButton
      className={cn(styles.navButton, styles.nextButton)}
      variant={isInside ? 'overlay-neutral' : 'secondary'}
      size="S"
      glyph="ChevronRight"
      onClick={() => emblaApi?.scrollNext()}
      disabled={!canScrollNext}
    />
  )

  const dots = showDots && hasMultipleSlides && (
    <div className={styles.dotsWrapper}>
      <div
        className={styles.dots}
        style={{ transform: `translateX(-${dotsOffset}px)` }}
      >
        {scrollSnaps.map((_, index) => (
          <button
            key={index}
            type="button"
            className={cn(styles.dot, { [styles.dotActive]: index === selectedIndex })}
            onClick={() => emblaApi?.scrollTo(index)}
            aria-label={`Slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  )

  return (
    <div
      {...attributes}
      className={cn(styles.carousel, { [styles.insideControls]: isInside }, className)}
      style={{
        ...style,
        '--carousel-slide-size': slideSize,
        '--carousel-slide-spacing': slideSpacing,
      } as React.CSSProperties}
    >
      {isInside && prevButton}
      <div className={styles.viewport} ref={emblaRef}>
        <div className={styles.slidesWrapper}>
          {children}
        </div>
      </div>
      {isInside && nextButton}
      {isInside && dots}
      {!isInside && (prevButton || nextButton || dots) && (
        <div className={styles.controlsArea}>
          {prevButton}
          {dots}
          {nextButton}
        </div>
      )}
    </div>
  )
}
